/**
 * Regrid Parcel Data — actual parcel boundaries with zoning.
 * PAID API — user provides their own API key.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout } from './types';

/**
 * Feature flag for Regrid integration. The Regrid API is paid + per-call
 * billed, so deployments that don't have a subscription should leave it
 * off entirely (vs. silently 401'ing against app.regrid.com on every
 * orchestrator run).
 *
 * Reads `VITE_REGRID_ENABLED` — `'true'` (case-insensitive) is the only
 * value that flips the flag on. Anything else (including unset, `'false'`,
 * `'0'`) keeps it disabled. Vite exposes `VITE_*` env vars on
 * `import.meta.env` automatically; no vite.config.ts change is needed.
 *
 * Reading via a function (not a top-level constant) keeps the flag
 * testable — Vitest can stub `import.meta.env.VITE_REGRID_ENABLED`
 * per-test by mutating `process.env` before the import resolves.
 */
export function isRegridEnabled(): boolean {
  // Vite injects `VITE_*` env vars at build time. In SSR / Node test
  // environments where `import.meta.env` is absent, fall back to
  // `process.env.VITE_REGRID_ENABLED` so the helper is callable without
  // bundling.
  const raw = (typeof import.meta !== 'undefined'
    && import.meta.env != null
    && typeof (import.meta.env as Record<string, unknown>).VITE_REGRID_ENABLED === 'string')
    ? (import.meta.env as Record<string, string>).VITE_REGRID_ENABLED
    : (typeof process !== 'undefined' ? process.env?.VITE_REGRID_ENABLED : undefined);
  return typeof raw === 'string' && raw.trim().toLowerCase() === 'true';
}

export interface ParcelData {
  parcelId: string;
  address: string;
  owner: string;
  acreage: number;
  zoning: string;
  landUse: string;
  assessedValue?: number;
}

export const regridParcelProvider: DataProvider<ParcelData> = {
  id: 'regrid-parcels',
  name: 'Regrid Parcel Data',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: true,

  async fetch(lat, lng, apiKey, geo?: ProviderGeoContext): Promise<ProviderResult<ParcelData>> {
    // Feature flag check first — deployments that haven't enabled
    // Regrid should return an explicit unavailable status, not a
    // 401-shaped error. The dispatcher catches throws and degrades
    // gracefully; this branch keeps the SED's `parcel` slot clean
    // with a parsable reason field.
    if (!isRegridEnabled()) {
      throw new Error('regrid_disabled');
    }
    if (!apiKey) throw new Error('Regrid API key required (paid subscription)');

    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`regrid-parcels` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. Parcel boundaries are crisp — a mailbox geocode can land in an
    // adjacent parcel entirely; the centroid is by construction inside the
    // drawn boundary, so it resolves the right parcel record.
    const url = `https://app.regrid.com/api/v2/parcels/query?lat=${lat}&lon=${lng}&token=${apiKey}&limit=1`;
    const res = await fetchWithTimeout(url, 15_000, geo?.signal);
    if (!res.ok) throw new Error(`Regrid: ${res.status}`);
    const json = await res.json();

    const feature = json.results?.[0] ?? json.features?.[0];
    if (!feature) throw new Error('Regrid returned no parcel data');

    const props = feature.properties || {};

    return {
      data: {
        parcelId: props.parcelnumb || props.id || 'Unknown',
        address: props.address || props.mailadd || 'Unknown',
        owner: props.owner || props.ownernme1 || 'Unknown',
        acreage: parseFloat(props.ll_gisacre || props.gisacre || '0'),
        zoning: props.zoning || props.zoning_description || 'Unknown',
        landUse: props.usedesc || props.land_use || 'Unknown',
        assessedValue: props.totalval ? parseFloat(props.totalval) : undefined,
      },
      sources: [{
        field: 'parcel',
        source: `Regrid — Parcel ${props.parcelnumb || 'N/A'}: ${props.usedesc || 'N/A'}, ${props.ll_gisacre || 'N/A'} acres, zoning: ${props.zoning || 'N/A'}`,
        url: 'https://regrid.com/',
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
